import { AxiosError } from 'axios';
import dayjs from 'dayjs';
import { FC, useContext } from 'react';

import { Box, Flex, LoadingOverlay, Text } from '@mantine/core';
import { useForm } from '@mantine/form';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { AppContext } from '@/contexts/AppContext/AppContextProvider';

import { personalPlacesApi } from '@/shared/api/personalPlaces';
import { TODAY } from '@/shared/constant/app';
import { useGetBookingTypeData } from '@/shared/hooks/useGetBookingTypeData';
import { useShowNotifaction } from '@/shared/hooks/useShowNotifaction';
import useAppStore from '@/shared/store/app.store.ts';
import { IRequestError, TDateRange } from '@/shared/types/app.types';
import { BookingType, IObject } from '@/shared/types/bookingObjects.types';
import { TPersonalPlaceActive } from '@/shared/types/personalPlaces.types';
import { BottomSheet } from '@/shared/uikit/components/bottomSheet';
import { ContentCheckbox } from '@/shared/uikit/components/checkbox/contentCheckbox';
import { LabelInput } from '@/shared/uikit/components/input';
import { ModalSheet } from '@/shared/uikit/components/modalSheet';
import { DateTimePicker } from '@/shared/uikit/components/picker/DateTimePicker/DateTimePicker';

type PersonalPlaceEditProps = {
	object: IObject & TPersonalPlaceActive;
	type: 'editFree' | 'editLimited';
	isAccess: boolean;
	opened: boolean;
	close: () => void;
};

type PersonalPlaceEditForm = {
	date: TDateRange;
	person_id: number;
	booking_object_id: number;
};

export const PersonalPlaceEdit: FC<PersonalPlaceEditProps> = ({
	object,
	type,
	isAccess,
	opened,
	close,
}) => {
	const queryClient = useQueryClient();
	const isMobile = useAppStore((state) => state.isMobile);
	const { user } = useContext(AppContext);
	const objectType = useGetBookingTypeData({
		bookingType: object?.type.kind as BookingType,
	});

	const form = useForm<PersonalPlaceEditForm>({
		initialValues: {
			date: [object.start, object.finish],
			person_id: user?.user.person_id,
			booking_object_id: object.id,
		},
		validate: {
			date: (value) => (value.every((date) => !date) ? 'Invalid date' : null),
		},
	});

	const { mutate: updateAccess, isPending: isUpdatingAccess } = useMutation({
		mutationFn: personalPlacesApi.updatePersonalPlaceAccess,
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: ['personalPlaces'] });
			useShowNotifaction({ text: 'Доступ обновлен', type: 'success' });
			close();
		},
		onError: (error: AxiosError<IRequestError>) => {
			useShowNotifaction({ text: error.response?.data?.message ?? '', type: 'error' });
		},
	});

	const { mutate: setAccessFree, isPending: isSettingAccessFree } = useMutation({
		mutationFn: personalPlacesApi.setPersonalPlaceFree,
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: ['personalPlaces'] });
			useShowNotifaction({ text: 'Место в свободном доступе', type: 'success' });
			close();
		},
		onError: (error: AxiosError<IRequestError>) => {
			useShowNotifaction({ text: error.response?.data?.message ?? '', type: 'error' });
		},
	});

	const handleGetAccess = () => {
		if (form.isValid()) {
			const { date, ...rest } = form.values;
			setAccessFree({ ...rest, start: date[0], finish: date[1] });
		}
	};

	const handleUpdateAccess = () => {
		if (form.isValid()) {
			const { date } = form.values;
			if (object.release_id) {
				updateAccess({ id: object.release_id, start: date[0], finish: date[1] });
			}
		}
	};

	const buttons = isAccess
		? [
				{
					children: 'Сохранить',
					loading: isUpdatingAccess,
					onClick: handleUpdateAccess,
				},
			]
		: [
				{
					children: 'Предоставить доступ',
					...(form.isValid()
						? {
								loading: isSettingAccessFree,
								onClick: handleGetAccess,
							}
						: { variant: 'light' }),
				},
			];

	const content = (
		<Box pos="relative">
			<LoadingOverlay visible={!object} zIndex={1000} overlayProps={{ radius: 2, blur: 2 }} />
			<Flex direction="column" gap={24}>
				<ContentCheckbox
					label={objectType?.label}
					leftIcon={objectType?.icon}
					subtitle="Персональное место"
					checked
				/>

				<DateTimePicker
					defaultTime={dayjs().toDate()}
					disabledDates={{
						minDate: TODAY.toDate(),
						maxDate: dayjs(TODAY).add(1, 'year').toDate(),
					}}
					{...form.getInputProps('date')}
				/>

				<Flex direction="column" gap={24}>
					<Flex direction="column" gap={16}>
						<Text fz={20} fw={800} lh="26px">
							Локация
						</Text>

						<LabelInput
							label="Офис и этаж"
							placeholder="Не выбрано"
							type="lock"
							defaultValue={object.description}
							multiple
							readOnly
						/>
					</Flex>

					<Flex direction="column" gap={16}>
						<Flex gap={8}>
							<LabelInput
								label={objectType?.label}
								type="lock"
								placeholder="Выбрать из списка"
								defaultValue={object.name.replace(/\s\|\s[\d\D]+/gm, '')}
								readOnly
							/>
						</Flex>
					</Flex>
				</Flex>

				{/*<MapButton text="Показать место на карте" disabled />*/}
			</Flex>
		</Box>
	);

	if (isMobile)
		return (
			<>
				<BottomSheet
					opened={opened}
					onClose={close}
					title={type === 'editFree' ? 'Свободный доступ' : 'Ограниченный доступ'}
					withCloseButton={false}
					buttons={buttons}
					withBackButton
					isFullScreen
				>
					{content}
				</BottomSheet>
			</>
		);

	return (
		<ModalSheet
			opened={opened}
			onClose={close}
			title={type === 'editFree' ? 'Свободный доступ' : 'Ограниченный доступ'}
			buttons={buttons}
			size="lg"
			h={600}
			withCloseButton
			centered
		>
			{content}
		</ModalSheet>
	);
};
